/**
 * CopilotDrawer Component
 * Drawer lateral com chat do Copilot para debug assistido
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { IconSparkles, IconX, IconSend } from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import AgentsFlowService from '../services/AgentsFlowService';

const CopilotDrawer = ({ agent, isOpen, onClose, focusedMessage, simulatorMessages, clearKey }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasSentAutoRef = useRef(false);

  // Limpar mensagens do copilot quando memória do agente é resetada
  useEffect(() => {
    if (clearKey > 0) {
      setMessages([]);
      setInputValue('');
      hasSentAutoRef.current = false;
    }
  }, [clearKey]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Auto-analysis when opening with a focused message
  useEffect(() => {
    if (isOpen && focusedMessage && focusedMessage.metadata?.debug_trace && !hasSentAutoRef.current) {
      hasSentAutoRef.current = true;
      sendMessage('Analise esta resposta do agente e o trace de execução. Identifique problemas e sugira melhorias.');
    }
  }, [isOpen, focusedMessage]);

  // Reset auto-send flag when focusedMessage changes
  useEffect(() => {
    hasSentAutoRef.current = false;
  }, [focusedMessage?.id]);

  const buildContext = useCallback(() => {
    const context = {
      graph_definition: agent?.graph?.graph_definition || {},
      conversation: (simulatorMessages || []).slice(-10).map(m => ({
        type: m.type,
        content: m.content,
      })),
      copilot_history: messages.map(m => ({
        role: m.role,
        content: m.content,
      })),
    };

    if (focusedMessage) {
      context.focused_message = {
        content: focusedMessage.content,
        metadata: {
          debug_trace: focusedMessage.metadata?.debug_trace || {},
        },
      };
    }

    return context;
  }, [agent, simulatorMessages, messages, focusedMessage]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || isLoading) return;

    const userMsg = { role: 'user', content: trimmed, id: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const context = buildContext();
      const res = await AgentsFlowService.copilot(agent.id, {
        message: trimmed,
        context,
      });

      const data = res.data || res;
      const assistantMsg = {
        role: 'assistant',
        content: data.response || 'Sem resposta do copilot.',
        id: Date.now() + 1,
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      console.error('Copilot error:', err);
      const errorContent = err.response?.data?.error || 'Erro ao se comunicar com o copilot. Tente novamente.';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `**Erro:** ${errorContent}`,
        id: Date.now() + 1,
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [agent, isLoading, buildContext]);

  const handleSend = useCallback(() => {
    sendMessage(inputValue);
    setInputValue('');
  }, [inputValue, sendMessage]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  if (!isOpen) return null;

  return (
    <div className="copilot-drawer">
      {/* Header */}
      <div className="copilot-header">
        <div className="copilot-header__title">
          <IconSparkles size={18} />
          Copilot
        </div>
        <button className="copilot-header__close" onClick={onClose}>
          <IconX size={18} />
        </button>
      </div>

      {/* Messages */}
      <div className="copilot-messages">
        {messages.length === 0 && !isLoading && (
          <div className="copilot-message copilot-message--assistant">
            Olá! Sou o Copilot. Posso analisar o comportamento do seu agente e sugerir melhorias.
            {focusedMessage
              ? ' Analisando a mensagem selecionada...'
              : ' Selecione uma mensagem do agente com o botão ✨ ou me pergunte algo.'}
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`copilot-message copilot-message--${msg.role}`}
          >
            {msg.role === 'assistant' ? (
              <ReactMarkdown>{msg.content}</ReactMarkdown>
            ) : (
              msg.content
            )}
          </div>
        ))}

        {isLoading && (
          <div className="copilot-loading">
            <span className="copilot-loading-dot" style={{ animationDelay: '0s' }} />
            <span className="copilot-loading-dot" style={{ animationDelay: '0.2s' }} />
            <span className="copilot-loading-dot" style={{ animationDelay: '0.4s' }} />
            Analisando...
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="copilot-input">
        <input
          ref={inputRef}
          type="text"
          placeholder="Pergunte sobre o agente..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
        />
        <button
          className="copilot-send"
          onClick={handleSend}
          disabled={!inputValue.trim() || isLoading}
        >
          <IconSend size={16} />
        </button>
      </div>
    </div>
  );
};

export default CopilotDrawer;
